import { optimizeImage } from "./image-optimizer";
import type { ImageCategory } from "@/types/schema";

/**
 * Generates an optimized image URL using the SSOT optimizer.
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  width: number, 
  height?: number
): string {
  if (!url) return '';
  
  return optimizeImage(url, {
    width,
    height,
    fit: height ? 'cover' : 'inside', // 'inside' mimics logic for width-only constraint
    quality: 80
  });
}

/**
 * Generates a dynamic image URL from Unsplash based on search term and category.
 * Falls back to a placeholder if term is missing.
 */
export function generateDynamicImageUrl(
  term: string | null | undefined,
  category: ImageCategory = "medical",
  width: number = 800,
  height: number = 600
): string {
  if (!term || term.trim() === "") {
    return `https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=${width}&h=${height}&fit=crop`;
  }

  // Clean and encode the search term
  const cleanTerm = term.trim().toLowerCase().replace(/[^a-z0-9\s]/g, "");
  
  // Add category-specific keywords for better image relevance
  const categoryKeywords: Record<ImageCategory, string> = {
    medical: "medical healthcare",
    person: "doctor physician portrait professional",
    building: "hospital clinic building architecture",
    wellness: "wellness spa health",
    equipment: "medical equipment technology",
  };

  const searchQuery = `${cleanTerm} ${categoryKeywords[category]}`;
  const encodedQuery = encodeURIComponent(searchQuery);

  // Use Unsplash Source API for dynamic images
  return `https://source.unsplash.com/featured/${width}x${height}/?${encodedQuery}`;
}

/**
 * Gets the appropriate image URL - uses stored URL if available, 
 * otherwise generates a dynamic one.
 */
export function getEntityImageUrl(
  storedUrl: string | null | undefined,
  fallbackTerm: string,
  category: ImageCategory = "medical",
  width: number = 800,
  height: number = 600
): string {
  if (storedUrl && storedUrl.trim() !== "") {
    // Optimization for LCP/SEO using SSOT
    return getOptimizedImageUrl(storedUrl, width, height);
  }
  return generateDynamicImageUrl(fallbackTerm, category, width, height);
}

/**
 * Generates alt text for accessibility based on entity type and name.
 */
export function generateAltText(
  name: string,
  entityType: "treatment" | "doctor" | "hospital"
): string {
  const prefixes: Record<string, string> = {
    treatment: "Medical procedure:",
    doctor: "Dr.",
    hospital: "Hospital:",
  };
  return `${prefixes[entityType]} ${name}`;
}

/**
 * Determines the best image category based on entity type.
 */
export function getCategoryForEntity(
  entityType: "treatment" | "doctor" | "hospital"
): ImageCategory {
  const mapping: Record<string, ImageCategory> = {
    treatment: "medical",
    doctor: "person",
    hospital: "building",
  };
  return mapping[entityType] || "medical";
}
